/**
 * Test DHIS2 tab – end-to-end testing of DHIS2 ambient AI flow.
 * Acts as a stand-in for the DHIS2 web form: start session with config_id,
 * mic sends ASR, live transcript + DHIS2 field extractions shown in right panel.
 * Does not touch the existing Consultation tab or global session.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { Mic, MicOff, PlayArrow, Stop, ChevronRight, ChevronLeft, Code, LiveTv, Science, CloudUpload } from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useTranscript } from '../../contexts/TranscriptContext';
import AmbXListener from '../AmbXListener/AmbXListener';
import LiveDiarizedTranscript from '../Transcript/LiveDiarizedTranscript';
import { sessionAPI, dhis2LabReportsAPI } from '../../services/api';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';
import { getWebSocketUrl } from '../../utils/websocket';
import type { Session } from '../../types/session';

export interface Dhis2FieldUpdate {
  type: string;
  section_id?: string;
  field_id?: string;
  data_element_id?: string;
  value?: unknown;
  value_type?: string;
  confidence?: number;
  source?: string;
}

function sameDisplayValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function upsertEffectiveExtraction(prev: Dhis2FieldUpdate[], incoming: Dhis2FieldUpdate): Dhis2FieldUpdate[] {
  const sectionId = incoming.section_id ?? '';
  const fieldId = incoming.field_id ?? '';
  if (!sectionId || !fieldId) {
    return prev;
  }

  const idx = prev.findIndex(
    (p) => (p.section_id ?? '') === sectionId && (p.field_id ?? '') === fieldId
  );
  if (idx < 0) {
    return [...prev, incoming];
  }

  const current = prev[idx];
  const sameValue = sameDisplayValue(current.value, incoming.value);
  const sameConfidence = Number(current.confidence ?? 0) === Number(incoming.confidence ?? 0);
  const sameSource = (current.source ?? '') === (incoming.source ?? '');

  // Do not duplicate identical effective events for the same field.
  if (sameValue && sameConfidence && sameSource) {
    return prev;
  }

  // Replace with latest winner event for this field (recent wins).
  const next = [...prev];
  next[idx] = incoming;
  return next;
}

export default function TestDhis2() {
  const { doctor, patient } = useUser();
  const { isListening, startListening: startTranscriptListening, stopListening: stopTranscriptListening } = useTranscript();

  const [configs, setConfigs] = useState<Dhis2ConfigOut[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [testSession, setTestSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractions, setExtractions] = useState<Dhis2FieldUpdate[]>([]);
  const [rightPanelTab, setRightPanelTab] = useState<'live' | 'extractions'>('live');
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [diarizedLines, setDiarizedLines] = useState<any[]>([]);
  // Live transcription buffer and lag (same as UnstructuredNotes – see TEST_DHIS2_LIVE_TRANSCRIPTION_LAG_ANALYSIS.md)
  const [bufferTranscription, setBufferTranscription] = useState('');
  const [bufferDiarization, setBufferDiarization] = useState('');
  const [remainingTimeTranscription, setRemainingTimeTranscription] = useState(0);
  const [remainingTimeDiarization, setRemainingTimeDiarization] = useState(0);
  const [transcriptionStatus, setTranscriptionStatus] = useState<string>('idle');
  const [labUploading, setLabUploading] = useState(false);
  const [labUploadError, setLabUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const wsDhis2Ref = useRef<WebSocket | null>(null);

  // Load DHIS2 configs on mount
  useEffect(() => {
    let cancelled = false;
    dhis2ConfigAPI
      .listConfigs()
      .then((res) => {
        if (!cancelled && res.data?.length) {
          setConfigs(res.data);
          if (!selectedConfigId && res.data[0]) setSelectedConfigId(res.data[0].id);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load configs');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll for WhisperLiveKit data; only show when data is for this Test DHIS2 session (not Consultation)
  useEffect(() => {
    if (!testSession?.session_id) return;
    const interval = setInterval(() => {
      const data = (window as any).__whisperLiveKitData;
      const dataSessionId = (window as any).__whisperLiveKitSessionId;
      const myId = String(testSession.session_id).toLowerCase();
      const dataId = dataSessionId != null ? String(dataSessionId).toLowerCase() : null;
      const isForThisSession = dataId != null && myId === dataId;
      if (data && isForThisSession) {
        setDiarizedLines(data.lines || []);
        if (isListening) {
          setBufferTranscription(data.buffer_transcription || '');
          setBufferDiarization(data.buffer_diarization || '');
          setRemainingTimeTranscription(data.remaining_time_transcription ?? 0);
          setRemainingTimeDiarization(data.remaining_time_diarization ?? 0);
          setTranscriptionStatus(data.status || 'active_transcription');
        }
      } else if (dataId != null && !isForThisSession) {
        setDiarizedLines([]);
        setBufferTranscription('');
        setBufferDiarization('');
        setRemainingTimeTranscription(0);
        setRemainingTimeDiarization(0);
        setTranscriptionStatus('idle');
      }
    }, 100);
    return () => clearInterval(interval);
  }, [testSession?.session_id, isListening]);

  // Connect to DHIS2 stream WebSocket when session is set
  useEffect(() => {
    if (!testSession?.session_id) {
      if (wsDhis2Ref.current) {
        wsDhis2Ref.current.close();
        wsDhis2Ref.current = null;
      }
      return;
    }
    const wsUrl = getWebSocketUrl(`/ws/forms/dhis2/${testSession.session_id}`);
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => console.log('[TestDhis2] DHIS2 stream WebSocket connected');
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'field_update') {
          setExtractions((prev) => upsertEffectiveExtraction(prev, msg));
        }
      } catch (_) {}
    };
    ws.onerror = (e) => console.error('[TestDhis2] DHIS2 WS error', e);
    ws.onclose = () => console.log('[TestDhis2] DHIS2 stream WebSocket closed');
    wsDhis2Ref.current = ws;
    return () => {
      ws.close();
      wsDhis2Ref.current = null;
    };
  }, [testSession?.session_id]);

  const handleStartSession = useCallback(async () => {
    if (!doctor || !patient) {
      setError('Please select a doctor and patient first (Select User in the header).');
      return;
    }
    if (!selectedConfigId) {
      setError('Please select a DHIS2 config.');
      return;
    }
    setLoading(true);
    setError(null);
    setExtractions([]);
    try {
      const res = await sessionAPI.create({
        clinician_id: doctor.doctor_id,
        patient_id: patient.patient_id,
        mode: 'ambient',
        audio_source_type: 'browser',
        config_id: selectedConfigId,
      });
      setTestSession(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  }, [doctor, patient, selectedConfigId]);

  const handleEndSession = useCallback(async () => {
    if (!testSession) return;
    setLoading(true);
    setError(null);
    try {
      await sessionAPI.complete(testSession.session_id);
      setTestSession(null);
      setExtractions([]);
      setDiarizedLines([]);
      setBufferTranscription('');
      setBufferDiarization('');
      setRemainingTimeTranscription(0);
      setRemainingTimeDiarization(0);
      setTranscriptionStatus('idle');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to end session');
    } finally {
      setLoading(false);
    }
  }, [testSession]);

  const handleLabUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !selectedConfigId) return;
      event.target.value = '';
      setLabUploadError(null);
      setLabUploading(true);
      try {
        const res = await dhis2LabReportsAPI.upload(
          selectedConfigId,
          file,
          testSession?.session_id ?? null
        );
        const data = res.data;
        if (data?.extractions?.length) {
          const asUpdates = data.extractions.map(
            (e: { section: string; field: string; value: unknown; confidence: number }) => ({
              type: 'field_update',
              section_id: e.section,
              field_id: e.field,
              value: e.value,
              confidence: e.confidence,
              source: 'lab_report',
            })
          );
          setExtractions((prev) =>
            asUpdates.reduce((acc, update) => upsertEffectiveExtraction(acc, update), prev)
          );
          setRightPanelTab('extractions');
        }
      } catch (err: any) {
        setLabUploadError(err.response?.data?.detail || err.message || 'Lab report upload failed');
      } finally {
        setLabUploading(false);
      }
    },
    [selectedConfigId, testSession?.session_id]
  );

  const combinedTranscripts = diarizedLines.map((line) => ({
    speaker: typeof line.speaker === 'number' ? line.speaker : parseInt(String(line.speaker), 10) || 0,
    text: line.text || '',
    start: line.start,
    end: line.end,
    detected_language: line.detected_language,
  }));

  const configName = configs.find((c) => c.id === selectedConfigId)?.name || selectedConfigId;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ flex: 1, overflow: 'hidden', minHeight: 0, p: 1 }}>
        {/* Left: Config + Session + Mic */}
        <Grid
          item
          xs={12}
          lg={panelCollapsed ? 12 : 8.5}
          sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >
          <Paper elevation={1} sx={{ p: 2, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1.5 }}>
              Test DHIS2 ambient AI flow
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a DHIS2 config, start a test session, then use the mic to send audio. Live transcript and
              DHIS2 field extractions appear in the right panel.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Tip:</strong> Speak at least 1–2 full sentences (e.g. &quot;The patient weight is 70 kg. Reason for visit is routine.&quot;) so extraction runs. Extractions also run when you stop the mic (final flush). Ensure <code>ENABLE_LLM_VALIDATION=true</code> in backend.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.tif"
                ref={fileInputRef}
                onChange={handleLabUpload}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={labUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedConfigId || labUploading}
              >
                {labUploading ? 'Uploading…' : 'Upload lab report'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Uses Lab Mapper + field synonyms; extractions appear below. With an active session, results are also stored and streamed via WebSocket.
              </Typography>
            </Box>
            {labUploadError && (
              <Alert severity="error" onClose={() => setLabUploadError(null)} sx={{ mb: 2 }}>
                {labUploadError}
              </Alert>
            )}

            <FormControl size="small" sx={{ minWidth: 280, mb: 2 }} fullWidth>
              <InputLabel>DHIS2 config</InputLabel>
              <Select
                value={selectedConfigId}
                label="DHIS2 config"
                onChange={(e) => setSelectedConfigId(e.target.value)}
                disabled={!!testSession}
              >
                {configs.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!testSession ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartSession}
                disabled={loading || !selectedConfigId || !doctor || !patient}
                startIcon={loading ? <CircularProgress size={16} /> : <PlayArrow />}
              >
                {loading ? 'Starting…' : 'Start test session'}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Tooltip title={testSession.session_id}>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    Session: {testSession.session_id.slice(0, 20)}…
                  </Typography>
                </Tooltip>
                <Chip
                  label={testSession.status}
                  size="small"
                  color={testSession.status === 'active' ? 'success' : 'default'}
                />
                <Chip label={configName} size="small" variant="outlined" />
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleEndSession}
                  disabled={loading || testSession.status === 'completed'}
                  startIcon={<Stop />}
                >
                  End session
                </Button>
              </Box>
            )}
          </Paper>

          {testSession && testSession.status === 'active' && (
            <Box sx={{ mb: 1 }}>
              <AmbXListener
                sessionId={testSession.session_id}
                audioSourceType="browser"
                onStart={() => startTranscriptListening?.()}
                onStop={() => stopTranscriptListening?.()}
                disabled={false}
              />
            </Box>
          )}

          {testSession && isListening && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }}
              />
              <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500 }}>
                Listening…
              </Typography>
            </Box>
          )}

          <Paper elevation={1} sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 120 }}>
            <Typography variant="body2" color="text.secondary">
              {testSession
                ? 'Use the mic above to speak. Live transcript and DHIS2 extractions appear in the right panel.'
                : 'Start a test session to begin. This tab uses the same ASR and extraction pipeline as the DHIS2 integration.'}
            </Typography>
          </Paper>
        </Grid>

        {/* Right panel: Live transcript + Extractions JSON */}
        {!panelCollapsed && (
          <Grid item xs={12} lg={3.5} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
            <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tabs
                  value={rightPanelTab}
                  onChange={(_, v) => setRightPanelTab(v)}
                  variant="fullWidth"
                  sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.8rem' } }}
                >
                  <Tab icon={<LiveTv fontSize="small" />} iconPosition="start" label={combinedTranscripts.length ? `Live (${combinedTranscripts.length})` : 'Live'} value="live" />
                  <Tab icon={<Code fontSize="small" />} iconPosition="start" label={extractions.length ? `Extractions (${extractions.length})` : 'Extractions'} value="extractions" />
                </Tabs>
                <Tooltip title="Hide panel">
                  <IconButton size="small" onClick={() => setPanelCollapsed(true)}>
                    <ChevronRight />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>
                {rightPanelTab === 'live' && (
                  <LiveDiarizedTranscript
                    lines={combinedTranscripts}
                    bufferTranscription={bufferTranscription}
                    bufferDiarization={bufferDiarization}
                    remainingTimeTranscription={remainingTimeTranscription}
                    remainingTimeDiarization={remainingTimeDiarization}
                    status={isListening ? transcriptionStatus : 'idle'}
                    isFinalizing={false}
                  />
                )}
                {rightPanelTab === 'extractions' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Field updates received from DHIS2 stream (ws/forms/dhis2/{testSession?.session_id?.slice(0, 8)}…)
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(extractions, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {panelCollapsed && (
          <Box sx={{ position: 'fixed', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1000 }}>
            <Tooltip title="Show Live & Extractions panel">
              <IconButton
                onClick={() => setPanelCollapsed(false)}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <ChevronLeft />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Grid>
    </Box>
  );
}
